<template>
    <NuxtPage/>
    <div v-if="!isProblemPage" class="w-full">
        <PageTitle>{{ t('title') }}</PageTitle>

        <YGroupContainer
            as="section"
            class="flex flex-col gap-6 sm:p-8"
            aria-labelledby="support-heading"
        >
                <div>
                    <h1 id="support-heading" class="max-w-2xl font-fancy text-3xl font-medium text-primary sm:text-4xl">
                        {{ t('heading') }}
                    </h1>
                    <p class="mt-4 max-w-2xl text-text-light">
                        {{ t('introduction') }}
                    </p>
                </div>

                <div class="grid gap-4 md:grid-cols-3">
                    <article class="flex flex-col rounded-lg border border-background-accent-dark bg-background p-5">
                        <div class="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-primary-light text-primary">
                            <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75 12 12.75l8.25-6M5.25 19.5h13.5A2.25 2.25 0 0 0 21 17.25V6.75a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6.75v10.5a2.25 2.25 0 0 0 2.25 2.25Z"/>
                            </svg>
                        </div>
                        <h2 class="font-fancy text-2xl font-bold text-text">{{ t('contact.title') }}</h2>
                        <p class="mt-2 grow text-sm leading-6 text-text-light">{{ t('contact.description') }}</p>
                        <a
                            href="mailto:info@yipp.nl?subject=YippCMS%20support"
                            class="mt-5 inline-flex w-fit items-center gap-2 rounded border border-primary bg-primary px-4 py-2 font-medium text-background transition-colors hover:bg-primary-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        >
                            <svg
                                class="h-4 w-4"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.8"
                                aria-hidden="true"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75 12 12.75l8.25-6M5.25 19.5h13.5A2.25 2.25 0 0 0 21 17.25V6.75a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6.75v10.5a2.25 2.25 0 0 0 2.25 2.25Z"/>
                            </svg>
                            {{ t('contact.action') }}
                        </a>
                    </article>

                    <article class="flex flex-col rounded-lg border border-background-accent-dark bg-background p-5">
                        <div class="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-primary-light text-primary">
                            <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 4.533A9.707 9.707 0 0 0 6 3c-1.052 0-2.062.167-3.009.476A.75.75 0 0 0 2.5 4.19v13.62c0 .511.498.873.985.72A8.706 8.706 0 0 1 6 18.167c1.878 0 3.616.59 5.04 1.594.273.193.647.193.92 0A8.702 8.702 0 0 1 17 18.167c.87 0 1.71.127 2.505.364a.75.75 0 0 0 .995-.72V4.19a.75.75 0 0 0-.491-.714A9.706 9.706 0 0 0 17 3a9.7 9.7 0 0 0-5.25 1.533v12.96"/>
                            </svg>
                        </div>
                        <h2 class="font-fancy text-2xl font-bold text-text">{{ t('website.title') }}</h2>
                        <p class="mt-2 grow text-sm leading-6 text-text-light">{{ t('website.description') }}</p>
                        <a
                            href="https://yipp.nl/"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="mt-5 inline-flex w-fit items-center gap-2 font-medium text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
                        >
                            {{ t('website.action') }}
                            <svg
                                class="h-4 w-4"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.8"
                                aria-hidden="true"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H18m0 0v4.5M18 6l-7.5 7.5"/>
                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 13.5v4.125A1.875 1.875 0 0 1 13.125 19.5h-6.75A1.875 1.875 0 0 1 4.5 17.625v-6.75A1.875 1.875 0 0 1 6.375 9H10.5"/>
                            </svg>
                        </a>
                    </article>

                    <article class="flex flex-col rounded-lg border border-destructive bg-destructive-light/10 p-5">
                        <div class="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-destructive-light/25 text-destructive">
                            <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9.303 3.376c.866 1.5-.217 3.374-1.948 3.374H4.645c-1.73 0-2.813-1.874-1.948-3.374L10.052 3.38c.865-1.5 3.03-1.5 3.896 0l7.355 12.746ZM12 16.5h.008v.008H12V16.5Z"/>
                            </svg>
                        </div>
                        <h2 class="font-fancy text-2xl font-bold text-destructive">{{ t('problem.title') }}</h2>
                        <p class="mt-2 grow text-sm leading-6 text-text-light">{{ t('problem.description') }}</p>
                        <NuxtLink
                            :to="localePath('/contact-support/problem')"
                            class="mt-5 inline-flex w-fit items-center justify-center rounded border border-destructive bg-destructive px-4 py-2 font-medium text-background transition-colors hover:bg-destructive-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-destructive"
                        >
                            {{ t('problem.action') }}
                        </NuxtLink>
                    </article>

                </div>
        </YGroupContainer>
    </div>
</template>

<script setup lang="ts">
const { t } = useI18n({ useScope: 'local' })
const appConfig = useAppConfig()
const localePath = useLocalePath()
const route = useRoute()

const isProblemPage = computed(() => route.path.replace(/\/$/, '').endsWith('/contact-support/problem'))

useHead(() => ({ title: `${ appConfig.headTitle } - ${ t('title') }` }))
</script>

<i18n lang="yaml">
en:
    title: Contact & Support
    heading: How can we help?
    introduction: Questions about YippCMS or running into a problem? Contact Yipp and we will help you find a solution.
    contact:
        title: Send us an email
        description: Describe your question or problem and include the details that can help us investigate it.
        action: "Email info{'@'}yipp.nl"
    website:
        title: About Yipp
        description: Learn more about Yipp and the digital products and experiences we create.
        action: Visit yipp.nl
    problem:
        title: I have a problem with this CMS
        description: Something is not working as expected? Tell us what happened so we can investigate it.
        action: Report a CMS problem
nl:
    title: Contact & Ondersteuning
    heading: Hoe kunnen we je helpen?
    introduction: Heb je een vraag over YippCMS of loop je ergens tegenaan? Neem contact op met Yipp, dan helpen we je een oplossing te vinden.
    contact:
        title: Stuur ons een e-mail
        description: Beschrijf je vraag of probleem en vermeld de gegevens die ons helpen om het te onderzoeken.
        action: "Mail info{'@'}yipp.nl"
    website:
        title: Over Yipp
        description: Lees meer over Yipp en de digitale producten en ervaringen die we maken.
        action: Bezoek yipp.nl
    problem:
        title: Ik heb een probleem met dit CMS
        description: Werkt er iets niet zoals verwacht? Vertel ons wat er is gebeurd, zodat we het kunnen onderzoeken.
        action: Meld een CMS-probleem
</i18n>
